import { EnquiryPayload, Property } from "@/interfaces/propertyinterface";
import { PropertyMetaData } from "@/interfaces/propertyMetadataInterface";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import { toast } from "react-toastify";

const API_URL = process.env.NEXT_PUBLIC_API_URL;

interface PropertyState {
  property: Property[];
  singleProperty: Property | null;
  propertyMetaData: PropertyMetaData[] | undefined;
  recommendedProperties: Property[];
  priceList: [];
  compareProperties: Property[];
  error: string | null;
  loading: boolean;
}

interface PostEnquiryResponse {
  data: string;
  status: number;
}

export interface FilterPayload {
  propertyName?: string;
  district?: string;
  minPrice?: string | null;
  maxPrice?: string | null;
  Tenure?: string;
  top?: string;
  TOP?: string;
  flatType?: string;
}

const initialState: PropertyState = {
  property: [],
  singleProperty: null,
  propertyMetaData: [],
  recommendedProperties: [],
  compareProperties: [],
  priceList: [],
  error: null,
  loading: false,
};

export const getAllProperties = createAsyncThunk(
  "property/getAllProperties",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/property`);
      return response.data?.results;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch properties"
        );
      } else {
        // Handle other types of errors, if necessary
        return rejectWithValue("An unknown error occurred");
      }
    }
  }
);

export const getSingleProperty = createAsyncThunk(
  "property/getSingleProperty",
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/property/get-single-property-details/${id}`
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch property"
        );
      } else {
        // Handle other types of errors, if necessary
        return rejectWithValue("An unknown error occurred");
      }
    }
  }
);

export const getSinglePropertyByName = createAsyncThunk(
  "property/getSinglePropertyByName",
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/property/get-property-by-name/${id}`
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch property"
        );
      } else {
        // Handle other types of errors, if necessary
        return rejectWithValue("An unknown error occurred");
      }
    }
  }
);

export const getPropertyByPincodes = createAsyncThunk(
  "property/getPropertyByPincodes",
  async (pincodes: string[], { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/property?addresses=${pincodes}`
      );
      return response?.data?.results;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch property"
        );
      } else {
        // Handle other types of errors, if necessary
        return rejectWithValue("An unknown error occurred");
      }
    }
  }
);

export const getPropertyMetaDataById = createAsyncThunk(
  "property/getMetaDataById",
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/property-meta-data/meta-data-by-property-id/${id}`
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch property"
        );
      } else {
        // Handle other types of errors, if necessary
        return rejectWithValue("An unknown error occurred");
      }
    }
  }
);

export const getFlatCategoriesByPropertyId = createAsyncThunk(
  "property/getFlatCategoriesByPropertyId",
  async (
    { propertyId }: { propertyId: string | undefined },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.get(
        `${API_URL}/property-meta-data/flats-with-categories/${propertyId}`
      );
      return response?.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch property Meta Data"
        );
      } else {
        // Handle other types of errors, if necessary
        return rejectWithValue("An unknown error occurred");
      }
    }
  }
);

export const postEnquiry = createAsyncThunk<
  PostEnquiryResponse, // Return type
  EnquiryPayload // Argument type
>("property/postEnquiry", async (data: EnquiryPayload, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}/enquiry`, data);
    toast.success(response?.data);
    const newResponse = {
      data: response?.data,
      status: response?.status,
    };
    return newResponse;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      toast.error(error?.response?.data?.message[0]);
      return rejectWithValue(
        error.response?.data?.message || "Failed to Post Enquiry"
      );
    } else {
      // Handle other types of errors, if necessary
      return rejectWithValue("An unknown error occurred");
    }
  }
});

export const getRecommendedProperty = createAsyncThunk(
  "properties/getRecommendedProperty",
  async (data: Property | undefined, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/property/recommended-property?longitude=${
          data?.location?.longitude
        }&latitude=${data?.location?.latitude}&radius=${20}`
      );
      return response.data;
    } catch (error: unknown) {
      // Cast error to AxiosError to access Axios-specific properties
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message ||
            "Failed to fetch Recommended property"
        );
      } else {
        // Handle other types of errors, if necessary
        return rejectWithValue("An unknown error occurred");
      }
    }
  }
);

export const getFilteredProperty = createAsyncThunk(
  "property/getFilteredProperty",
  async (data: FilterPayload, { rejectWithValue }) => {
    try {
      const baseUrl = `${API_URL}/property/master-filter`;

      // Construct query parameters dynamically
      const queryParams = new URLSearchParams();
      if (data?.propertyName)
        queryParams.append("property_name", data?.propertyName);
      // if (data?.location) queryParams.append("pin_code", data?.location);
      if (data?.maxPrice) queryParams.append("price_max", data.maxPrice);
      if (data?.minPrice) queryParams.append("price_min", data.minPrice);
      if (data?.top) queryParams.append("top", data.top);
      if (data?.flatType) queryParams.append("type", data.flatType);

      // Handle tenure conditions
      if (data?.Tenure) {
        if (data.Tenure === "Freehold") {
          queryParams.append("is_leasehold", "false");
        } else {
          queryParams.append("is_leasehold", "true");
          queryParams.append("lease_year", data.Tenure);
        }
      }

      // Full URL with dynamic query parameters
      const url = queryParams.toString()
        ? `${baseUrl}?${queryParams.toString()}`
        : baseUrl;

      // API call
      const response = await axios.get(url);
      return response.data.results;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch tenure List"
        );
      } else {
        // Handle other types of errors, if necessary
        return rejectWithValue("An unknown error occurred");
      }
    }
  }
);

export const getCompareProperty = createAsyncThunk(
  "property/getCompareProperty",
  async (
    {
      latitude,
      longitude,
    }: { latitude: number | undefined; longitude: number | undefined },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.get(
        `${API_URL}/property/compare-property?longitude=${longitude}&latitude=${latitude}&radius=20`
      );
      return response.data.results;
    } catch (error: unknown) {
      // Cast error to AxiosError to access Axios-specific properties
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch Compare property"
        );
      } else {
        // Handle other types of errors, if necessary
        return rejectWithValue("An unknown error occurred");
      }
    }
  }
);

export const sendNotificationOnTelegram = createAsyncThunk(
  "property/sendNotificationOnTelegram",
  async (data: { message: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_CHAT_BOT_API_URL}/send-message`,
        { message: data.message }
      );

      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message ||
            "Failed to send Telegram notification"
        );
      }
      return rejectWithValue("An unknown error occurred");
    }
  }
);

export const getPriceList = createAsyncThunk(
  "property",
  async ({ id }: { id: string | undefined }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/property-meta-data/export-pricelist/${id}`
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return (
          rejectWithValue(error?.response?.data?.message) ||
          "Failed to fetch Price List"
        );
      }
    }
    return rejectWithValue("An unkown error occurred");
  }
);

const propertySlice = createSlice({
  name: "property",
  initialState,
  reducers: {
    test: () => {
      console.log("test property");
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllProperties.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getAllProperties.fulfilled, (state, action) => {
      state.loading = false;
      state.property = action.payload;
    });
    builder.addCase(getAllProperties.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(getSingleProperty.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getSingleProperty.fulfilled, (state, action) => {
      state.loading = false;
      state.singleProperty = action.payload;
    });
    builder.addCase(getSingleProperty.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(getSinglePropertyByName.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getSinglePropertyByName.fulfilled, (state, action) => {
      state.loading = false;
      state.singleProperty = action.payload;
    });
    builder.addCase(getSinglePropertyByName.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(getPropertyByPincodes.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getPropertyByPincodes.fulfilled, (state, action) => {
      state.loading = false;
      state.property = action.payload;
    });
    builder.addCase(getPropertyByPincodes.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(getPropertyMetaDataById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getPropertyMetaDataById.fulfilled, (state, action) => {
      state.loading = false;
      state.propertyMetaData = action.payload;
    });
    builder.addCase(getPropertyMetaDataById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(getFlatCategoriesByPropertyId.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      getFlatCategoriesByPropertyId.fulfilled,
      (state, action) => {
        state.loading = false;
        state.propertyMetaData = action.payload;
      }
    );
    builder.addCase(getFlatCategoriesByPropertyId.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(postEnquiry.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(postEnquiry.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(postEnquiry.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(getRecommendedProperty.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getRecommendedProperty.fulfilled, (state, action) => {
      state.loading = false;
      state.recommendedProperties = action.payload;
    });
    builder.addCase(getRecommendedProperty.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(getFilteredProperty.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getFilteredProperty.fulfilled, (state, action) => {
      state.loading = false;
      state.property = action.payload;
    });
    builder.addCase(getFilteredProperty.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(getCompareProperty.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getCompareProperty.fulfilled, (state, action) => {
      state.loading = false;
      state.compareProperties = action.payload;
    });
    builder.addCase(getCompareProperty.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(sendNotificationOnTelegram.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(sendNotificationOnTelegram.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(sendNotificationOnTelegram.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(getPriceList.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getPriceList.fulfilled, (state, action) => {
      state.loading = false;
      state.priceList = action.payload;
    });
    builder.addCase(getPriceList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { test } = propertySlice.actions;
export default propertySlice.reducer;
